import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Skeleton,
  Autocomplete,
  InputAdornment,
  MenuItem,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import 'date-fns';
import styles from './styles.module.css';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import EditIcon from '@mui/icons-material/Edit';
import ConfirmProduction from 'components/popups/CofirmProduction';
import { InventoryContext } from 'Context/InventoryContext';
import { ITEM_TYPES, ROUTES } from 'const';
import { featureIsExpired } from 'components/utils/FeatureValidityExpire';
import NumberField from 'components/library/NumberField';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from 'react-i18next';
import BatchSelectionPopup from 'components/popups/BatchSelectionPopup';
import { AlertMessagesContext } from 'react-alert-messages';
import { Collapse, Box, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { WarehouseContext } from 'Context/WarehouseContext';
import { ShopContext } from 'Context/ShopContext';
import ShopsHelper from 'helpers/ShopsHelper';
import { useHistory } from 'react-router-dom';

function Production() {
  const { t: tCommon } = useTranslation('common');
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { products, getProducts, getStockBatches, stockBatches } = useContext(InventoryContext);
  const history = useHistory();
  const { getWarehouses, warehouses, getWarehouseProducts, warehouseProducts } = useContext(WarehouseContext);
  const { shop } = useContext(ShopContext);

  const [editSelectedData, setEditSelectedData] = useState({});
  const [item, setItem] = useState(null);
  const [productionItems, setProductionItems] = useState([]);
  const [plu, setPlu] = useState('');
  const [name, setName] = useState('');
  const [cost, setCost] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [gst, setGst] = useState(0);
  const [stock, setStock] = useState(0);
  const [showConfirmProductionDialog, setShowConfirmProductionDialog] = useState(false);
  const [productionItemsData, setProductionItemsData] = useState([]);
  const [warehouseId, setWarehouseId] = useState(0);
  const [date, setDate] = useState(new Date());
  const [batchName, setBatchName] = useState('');
  const [batchPopup, setBatchPopup] = useState({
    open: false,
    anchorEl: null,
    item: null,
    subItem: null,
    productionItemIndex: null,
  });

  useEffect(() => {
    setBatchName(ShopsHelper.generateBatchName(date));
  }, [date]);

  useEffect(() => {
    getStockBatches();
  }, [getStockBatches]);

  useEffect(() => {
    getStockBatches();
  }, [getStockBatches]);

  useEffect(() => {
    if (ShopsHelper.isWarehouseFeatureActive(shop)) {
      getWarehouses({ useCache: true });
      getWarehouseProducts({ useCache: true });
    }
  }, [shop, getWarehouses, getWarehouseProducts]);

  useEffect(() => {
    if (ShopsHelper.isWarehouseFeatureActive(shop) && warehouseId === 0) {
      if (shop?.config?.default_warehouse && warehouses?.length > 0) {
        const defaultWh = warehouses.find((w) => w.id === shop.config.default_warehouse);
        if (defaultWh) {
          setWarehouseId(defaultWh.id);
        }
      }
    }
  }, [shop, warehouses, warehouseId]);

  const filteredStockBatches = useMemo(() => {
    const currentWhId = warehouseId === 0 ? null : warehouseId;
    if (!currentWhId) return stockBatches || [];
    return stockBatches.filter((b) => {
      const bWhId = b.warehouse?.id || b.warehouse || null;
      return String(bWhId) === String(currentWhId);
    });
  }, [warehouseId, stockBatches]);

  useEffect(() => {
    setProductionItems((products || [])?.filter((item) => item.type === ITEM_TYPES.PRODUCTION));
  }, [products]);

  const getWarehouseStock = (itemId) => {
    if (warehouseId === 0) return products?.find((p) => p.id === itemId)?.stock || 0;
    const whProd = warehouseProducts.find(
      (wp) =>
        String(wp.item?.id || wp.item) === String(itemId) &&
        String(wp.warehouse?.id || wp.warehouse) === String(warehouseId)
    );
    return whProd?.stock || 0;
  };

  const handleChangeRate = (val) => {
    if (quantity === 0 || item === null) return;
    setCost(val);
  };

  const handleChangeQty = (val) => {
    if (item === null) return;
    setQuantity(val);
    const whStock = getWarehouseStock(item.id);
    setStock(whStock + Number(val));
    setCost(item?.cost * Number(val));
  };

  const resetItemFields = () => {
    setName('');
    setPlu('');
    setCost('');
    setQuantity(1);
    setGst('');
    setItem(null);
    setStock('');
  };
  const addItem = () => {
    const sub_item = (item.details?.sub_items || []).map((subItem) => {
      const prod = (products || []).find((p) => String(p.id) === String(subItem.item_id));
      const requiredQty = subItem.fraction * quantity;

      let selectedBatches = [];
      if (prod?.is_batch_enabled) {
        // Auto-allocate batches
        const availableBatches = (filteredStockBatches || [])
          .filter((b) => {
            const isSameItem = String(b.item) === String(subItem.item_id);
            if (!isSameItem) return false;

            // Expiry check: treat 'Z' as local time for accurate comparison
            const expiryDate = b.expiry_date ? new Date(b.expiry_date.replace('Z', '')) : null;
            const isExpired = expiryDate && expiryDate <= new Date();
            return !isExpired;
          })
          .sort((a, b) => {
            if (!a.expiry_date) return 1;
            if (!b.expiry_date) return -1;
            return new Date(a.expiry_date) - new Date(b.expiry_date);
          });

        let remainingToAllocate = requiredQty;
        for (const batch of availableBatches) {
          if (remainingToAllocate <= 0) break;
          const take = Math.min(remainingToAllocate, batch.stock || 0);
          if (take > 0) {
            selectedBatches.push({ batch_id: batch.id, quantity: take, batch_no: batch.batch_no || batch.batch_name });
            remainingToAllocate -= take;
          }
        }
      }

      return {
        id: subItem.id, // ID from details
        item_id: subItem.item_id,
        name: subItem.name || prod?.name || '-',
        quantity: requiredQty,
        fraction: subItem.fraction,
        cost: prod?.cost || 0,
        totalCost: requiredQty * (prod?.cost || 0),
        is_batch_enabled: prod?.is_batch_enabled,
        batch_data: selectedBatches,
      };
    });

    let data = {
      plu: item.plu,
      item: item,
      rate: Number(cost),
      quantity: Number(quantity),
      vat: Number(gst),
      stock: Number(stock),
      sub_item,
    };
    if (editSelectedData && !(editSelectedData?.items || []).find((itm) => itm.item === item && itm.rate === cost)) {
      data = {
        ...data,
        purchase: editSelectedData.id,
        new_item: true,
      };
    } else {
      const updatingItem = (editSelectedData?.items || []).find((itm) => itm.item === item && itm.rate === cost);
      data = {
        ...updatingItem,
        ...data,
      };
    }

    setProductionItemsData([...productionItemsData, data]);
    resetItemFields();
  };

  const dltItem = (item) => {
    setProductionItemsData(productionItemsData.filter((itm) => itm.rate !== item.rate && itm.plu !== item.plu));
  };

  const handleEditItem = (productionItem) => {
    setEditSelectedData(productionItem);
    setItem(productionItem.item);
    setName(productionItem.item?.name);
    setPlu(productionItem?.plu);
    setQuantity(productionItem?.quantity);
    setCost(productionItem?.rate);
    setGst(productionItem?.vat);
    setProductionItemsData(
      productionItemsData.filter((itm) => itm.rate !== productionItem.rate && itm.plu !== productionItem.plu)
    );
    setStock(getWarehouseStock(productionItem?.item.id) + Number(productionItem?.quantity));
  };

  const handleOpenBatchPopup = (event, prodItemIndex, subItem) => {
    const prod = (products || []).find((p) => String(p.id) === String(subItem.item_id));
    setBatchPopup({
      open: true,
      anchorEl: event.currentTarget,
      item: prod,
      subItem: subItem,
      productionItemIndex: prodItemIndex,
    });
  };

  const handleBatchChange = (batchId, val) => {
    const { productionItemIndex, subItem } = batchPopup;
    const qtyValue = parseFloat(val) || 0;

    const batch = (filteredStockBatches || []).find((b) => String(b.id) === String(batchId));
    const availableStock = batch?.stock || 0;

    let finalQty = qtyValue;
    if (finalQty > availableStock) {
      postAlertMessage({
        text: `Quantity cannot exceed available stock (${availableStock}) for batch ${
          batch?.batch_no || batch?.batch_name || batchId
        }`,
        type: 'failed',
      });
      finalQty = availableStock;
    }

    setProductionItemsData((prev) => {
      const next = [...prev];
      const targetProdItem = { ...next[productionItemIndex] };
      // Deep clone sub_item array to ensure immutability
      const nextSubItemArray = (targetProdItem.sub_item || []).map((si) => ({ ...si }));

      const subItemIndex = nextSubItemArray.findIndex((si) => si.item_id === subItem.item_id);
      if (subItemIndex === -1) return prev;

      const updatedSubItem = { ...nextSubItemArray[subItemIndex] };
      const newBatches = [...(updatedSubItem.batch_data || [])];
      const batchIndex = newBatches.findIndex((b) => String(b.batch_id) === String(batchId));

      if (batchIndex > -1) {
        if (finalQty > 0) {
          newBatches[batchIndex] = { ...newBatches[batchIndex], quantity: finalQty };
        } else {
          newBatches.splice(batchIndex, 1);
        }
      } else if (finalQty > 0) {
        newBatches.push({
          batch_id: batchId,
          quantity: finalQty,
          batch_no: batch?.batch_no || batch?.batch_name || '-',
        });
      }

      updatedSubItem.batch_data = newBatches;
      nextSubItemArray[subItemIndex] = updatedSubItem;
      targetProdItem.sub_item = nextSubItemArray;
      next[productionItemIndex] = targetProdItem;

      setBatchPopup((prevPopup) => ({
        ...prevPopup,
        subItem: updatedSubItem,
      }));

      return next;
    });
  };

  const getInvalidProductionItem = () => {
    if (productionItemsData.length === 0) return null;

    const invalidItem = productionItemsData.find((prodItem) =>
      (prodItem.sub_item || []).some((si) => {
        if (!si.is_batch_enabled) return false;

        const totalSelected = (si.batch_data || []).reduce((sum, b) => sum + (b.quantity || 0), 0);
        return Math.abs(si.quantity - totalSelected) >= 0.001;
      })
    );
    return invalidItem;
  };

  useEffect(() => {
    getProducts();
  }, [getProducts]);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <h2 className={styles.title}>
          {tCommon('Production')}
          <span className={styles.menuTitle}>{tCommon('Management')}</span>
        </h2>
      </div>
      <div className={styles.changeable}>
        <Grid container>
          <Grid
            item
            container
            xs={12}
            sm={4}
            alignItems="center"
            className={styles.gridContainer}
            style={{ paddingLeft: '0' }}
          >
            <Grid item xs={4} sm={5} className={styles.inputLabelContainer}>
              <h3 className={styles.inputLabel}>Date</h3>
            </Grid>
            <Grid item xs={8} sm={7}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  format="dd/MM/yyyy"
                  value={date}
                  onChange={(date) => {
                    setDate(date);
                  }}
                  slotProps={{
                    textField: {
                      variant: 'outlined',
                      id: 'date-purchase',
                      size: 'small',
                      fullWidth: true,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={ShopsHelper.isWarehouseFeatureActive(shop) ? 4 : 6}>
            <TextField
              label={tCommon('Batch Name')}
              variant="outlined"
              size="small"
              fullWidth
              value={batchName}
              onChange={(e) => setBatchName(e.target.value)}
            />
          </Grid>
          {ShopsHelper.isWarehouseFeatureActive(shop) && (
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                size="small"
                variant="outlined"
                label={tCommon('Warehouse')}
                value={warehouseId}
                onChange={(e) => {
                  setWarehouseId(Number(e.target.value));
                  setProductionItemsData([]);
                  resetItemFields();
                }}
              >
                <MenuItem value={0}>
                  <em>{tCommon('None')}</em>
                </MenuItem>
                {warehouses?.map((wh) => (
                  <MenuItem key={wh.id} value={wh.id}>
                    {wh.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
        </Grid>
      </div>

      <div className={styles.changeable} style={{ boxShadow: 'none', padding: '0', overflowX: 'auto' }}>
        <Table>
          <TableRow key="input row">
            <TableCell>
              <Autocomplete
                value={item}
                onChange={(event, newItem) => {
                  setItem(newItem);
                  setName(newItem.name || '');
                  setCost(newItem?.cost || '');
                  setStock(getWarehouseStock(newItem.id));
                }}
                disableClearable
                inputValue={plu}
                onInputChange={(e, value) => {
                  setPlu(value);
                }}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                getOptionLabel={(option) =>
                  typeof option === 'object' && option !== null && option.plu ? String(option.plu) : ''
                }
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.plu || 'N/A'}
                  </li>
                )}
                filterOptions={(options, { inputValue }) =>
                  (options || []).filter(
                    (option) =>
                      String(option.plu).includes(inputValue) ||
                      (option.ean && option.ean.toLowerCase().includes(inputValue.toLowerCase()))
                  )
                }
                options={productionItems}
                renderInput={(params) => (
                  <TextField {...params} label="PLU" variant="outlined" size="small" fullWidth />
                )}
              />
            </TableCell>
            <TableCell>
              <Autocomplete
                value={item}
                onChange={(event, newItem) => {
                  setItem(newItem);
                  setPlu(String(newItem.plu) || '');
                  setCost(newItem?.cost || '');
                  setStock(getWarehouseStock(newItem.id));
                }}
                disableClearable
                inputValue={name}
                onInputChange={(e, value) => {
                  setName(value);
                }}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.name || '')}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                      <span>{option.name}</span>
                      <span style={{ color: '#718096', fontSize: '12px' }}>(PLU: {option.plu})</span>
                    </Box>
                  </li>
                )}
                filterOptions={(options, { inputValue }) =>
                  (options || []).filter(
                    (option) =>
                      option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                      String(option.plu).toLowerCase().includes(inputValue.toLowerCase()) ||
                      (option.ean && option.ean.toLowerCase().includes(inputValue.toLowerCase()))
                  )
                }
                options={productionItems}
                renderInput={(params) => (
                  <TextField {...params} label="Name" variant="outlined" size="small" fullWidth />
                )}
                style={{ minWidth: '240px' }}
              />
            </TableCell>
            <TableCell>
              <NumberField
                label={tCommon('Cost')}
                value={cost}
                onChange={(e) => handleChangeRate(e.target.value)}
                disabled
                style={{ minWidth: '75px' }}
              />
            </TableCell>
            <TableCell>
              <NumberField
                label={tCommon('Quantity')}
                value={quantity}
                onChange={(e) => handleChangeQty(e.target.value)}
                style={{ minWidth: '75px' }}
              />
            </TableCell>
            <TableCell>
              <TextField
                label={tCommon('Stock')}
                variant="outlined"
                size="small"
                fullWidth
                disabled
                value={stock}
                style={{ minWidth: '75px' }}
              />
            </TableCell>
            <TableCell>
              <IconButton disabled={plu === '' || name === ''} onClick={addItem}>
                <LibraryAddIcon />
              </IconButton>
            </TableCell>
            <TableCell></TableCell>
          </TableRow>
        </Table>
      </div>

      <TableContainer style={{ marginTop: 4 }} component={Paper}>
        <Table aria-label="customized table">
          <TableHead>
            <TableRow>
              <TableCell className={styles.columnCell}>{tCommon('PLU')}</TableCell>
              <TableCell className={styles.nameColumn}>{tCommon('Name')}</TableCell>
              <TableCell className={styles.columnCell}>{tCommon('Cost')}</TableCell>
              <TableCell className={styles.columnCell}>{tCommon('Quantity')}</TableCell>
              <TableCell className={styles.columnCell}>{tCommon('Stock')}</TableCell>
              <TableCell className={styles.columnCell}> {tCommon('Actions')}</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products === null &&
              [...Array(3)].map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Skeleton variant="text" animation="wave" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" animation="wave" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" animation="wave" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" animation="wave" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" animation="wave" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" animation="wave" />
                  </TableCell>
                  <TableCell />
                </TableRow>
              ))}
            {productionItemsData &&
              productionItemsData.map((productionItem, index) => (
                <ProductionItemRow
                  key={index}
                  productionItem={productionItem}
                  index={index}
                  dltItem={dltItem}
                  handleEditItem={handleEditItem}
                  handleOpenBatchPopup={handleOpenBatchPopup}
                  tCommon={tCommon}
                  getWarehouseStock={getWarehouseStock}
                />
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <BatchSelectionPopup
        open={batchPopup.open}
        anchorEl={batchPopup.anchorEl}
        onClose={() => setBatchPopup({ ...batchPopup, open: false })}
        item={batchPopup.item}
        stockBatches={filteredStockBatches}
        selectedBatches={batchPopup.subItem?.batch_data || []}
        onChange={handleBatchChange}
        showExpired={false}
      />
      <Grid container className={styles.submitSection} justify="flex-end">
        <Button
          variant="contained"
          color="secondary"
          className={styles.closeBtn}
          style={{ backgroundColor: '#f50057' }}
          disabled={productionItemsData.length === 0}
          onClick={() => {
            setProductionItemsData([]);
          }}
        >
          {tCommon('Cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          style={{ backgroundColor: '#00a65a' }}
          disabled={productionItemsData.length === 0}
          onClick={() => {
            const invalidItem = getInvalidProductionItem();
            if (invalidItem) {
              postAlertMessage({
                text: `Total batch quantity must match the required quantity for raw materials in "${invalidItem.item?.name}".`,
                type: 'failed',
              });
              return;
            }
            setShowConfirmProductionDialog(true);
          }}
        >
          {tCommon('Save Production')}
        </Button>
        {showConfirmProductionDialog && (
          <ConfirmProduction
            data={{
              items: productionItemsData,
              date,
              batch_name: batchName,
              warehouse_id: warehouseId === 0 ? null : warehouseId,
            }}
            handleClose={() => setShowConfirmProductionDialog(false)}
            handleSuccess={async () => {
              const isPremium = ShopsHelper.hasProAccess(shop);
              const hasBatchEnabled = productionItemsData.some((pi) => {
                const mainBatch = pi.item?.is_batch_enabled;
                const subBatch = (pi.sub_item || []).some((si) => si.is_batch_enabled);
                return mainBatch || subBatch;
              });
              const isWarehouseEnabled =
                ShopsHelper.isWarehouseFeatureActive(shop) || !featureIsExpired(shop?.warehouse_validity);

              const promises = [getProducts({ useCache: false })];
              if (isPremium && hasBatchEnabled) {
                promises.push(getStockBatches({ useCache: false }));
              }
              if (isWarehouseEnabled) {
                promises.push(getWarehouseProducts({ useCache: false }));
              }

              setProductionItemsData([]);
              setShowConfirmProductionDialog(false);

              await Promise.all(promises);

              const newDate = new Date();
              setDate(newDate);
              setBatchName(ShopsHelper.generateBatchName(newDate));
              history.push(ROUTES.PRODUCTION_HISTORY);
            }}
          />
        )}
      </Grid>
    </div>
  );
}

function ProductionItemRow({
  productionItem,
  index,
  dltItem,
  handleEditItem,
  handleOpenBatchPopup,
  tCommon,
  getWarehouseStock,
}) {
  const [open, setOpen] = useState(true);

  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          {productionItem.plu}
        </TableCell>
        <TableCell>{productionItem.item.name}</TableCell>
        <TableCell>{productionItem.rate}</TableCell>
        <TableCell>{productionItem.quantity}</TableCell>
        <TableCell>{productionItem.stock}</TableCell>
        <TableCell colSpan={2} style={{ display: 'flex' }}>
          <IconButton onClick={() => dltItem(productionItem)}>
            <DeleteIcon />
          </IconButton>
          <IconButton onClick={() => handleEditItem(productionItem)}>
            <EditIcon />
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={1}>
              <Typography variant="h6" gutterBottom component="div" style={{ fontSize: '0.9rem', fontWeight: 600 }}>
                {tCommon('Raw Materials')}
              </Typography>
              <Table size="small" aria-label="purchases">
                <TableHead>
                  <TableRow>
                    <TableCell style={{ fontWeight: 600 }}>{tCommon('Name')}</TableCell>
                    <TableCell style={{ fontWeight: 600 }}>{tCommon('Warehouse Stock')}</TableCell>

                    <TableCell style={{ fontWeight: 600 }}>{tCommon('Required Qty')}</TableCell>
                    <TableCell style={{ fontWeight: 600 }}>{tCommon('Selected Batches')}</TableCell>
                    <TableCell style={{ fontWeight: 600 }}>{tCommon('Remaining Qty')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(productionItem.sub_item || []).map((si) => {
                    const totalSelected = (si.batch_data || []).reduce((sum, b) => sum + (b.quantity || 0), 0);
                    const whStock = getWarehouseStock(si.item_id);

                    const remaining = si.is_batch_enabled
                      ? Math.max(0, si.quantity - totalSelected)
                      : Math.max(0, si.quantity - whStock);
                    return (
                      <TableRow key={si.item_id}>
                        <TableCell>
                          {si.name}
                          {si.is_batch_enabled && si.batch_data && si.batch_data.length > 0 && (
                            <div style={{ fontSize: '0.8rem', color: '#666' }}>
                              {si.batch_data.map((sb, idx) => (
                                <span key={sb.batch_id}>
                                  {sb.batch_no || sb.batch_id}: {sb.quantity.toFixed(3)}
                                  {idx < si.batch_data.length - 1 ? ', ' : ''}
                                </span>
                              ))}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>{whStock.toFixed(3)}</TableCell>
                        <TableCell>{si.quantity.toFixed(3)}</TableCell>
                        <TableCell>
                          {si.is_batch_enabled ? (
                            <NumberField
                              value={totalSelected === 0 ? '' : totalSelected}
                              style={{ minWidth: '100px', cursor: 'pointer' }}
                              sx={{ '& input': { cursor: 'pointer' } }}
                              inputProps={{ readOnly: true }}
                              onKeyDown={(e) => e.preventDefault()}
                              InputProps={{
                                onClick: (e) => {
                                  handleOpenBatchPopup(e, index, si);
                                },
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton color="primary" size="small">
                                      <ArrowDropDownIcon fontSize="small" />
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                            />
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell style={{ color: Math.abs(remaining) > 0.001 ? 'red' : 'inherit' }}>
                          {si.is_batch_enabled ? remaining.toFixed(3) : '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default withConsoleBase(Production);
